/**
 * Textarea Component
 * Uses Uber Base design tokens
 */

import { forwardRef, type TextareaHTMLAttributes } from 'react'
import { cn } from '../../utils/helpers'

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  error?: string
  helperText?: string
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, label, error, helperText, id, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-')

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-label-small text-content-primary mb-1"
          >
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={inputId}
          className={cn(
            'w-full min-h-[100px] px-4 py-3',
            'text-paragraph-medium text-content-primary',
            'bg-bg-primary',
            'border rounded-sm',
            'transition-colors duration-150',
            'placeholder:text-content-tertiary',
            'focus:outline-none focus:ring-2 focus:ring-offset-0',
            'resize-y',
            error
              ? 'border-negative focus:ring-negative/20'
              : 'border-border-default focus:border-border-selected focus:ring-uber-black/10',
            'disabled:bg-gray-50 disabled:cursor-not-allowed',
            className
          )}
          {...props}
        />
        {error && (
          <p className="mt-1 text-paragraph-xsmall text-negative">{error}</p>
        )}
        {helperText && !error && (
          <p className="mt-1 text-paragraph-xsmall text-content-tertiary">
            {helperText}
          </p>
        )}
      </div>
    )
  }
)

Textarea.displayName = 'Textarea'

export { Textarea }
